import { useIsMutating } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Download, Trash2 } from "lucide-react";
import { useState } from "react";

import type { FeedbackAttachment } from "@/features/request/feedback-tool/feedbackTool/types";

import { Loading } from "@/features/request/feedback-tool/feedbackTool/components/Loading.tsx";
import { useDeleteFeedbackAttachment } from "@/features/request/feedback-tool/feedbackTool/hooks/useDeleteFeedbackAttachment.tsx";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import { useHandleDownload } from "@/features/request/feedback-tool/feedbackTool/hooks/useHandleDownload.tsx";
import { isImageFile } from "@/features/request/feedback-tool/feedbackTool/utils";
import { feedbackToolKeys } from "@/hooks/queryKeys.ts";

interface AssetProps {
  attachment: FeedbackAttachment;
  isActive: boolean;
  isSelected: boolean;
}

export default function Asset({
  attachment,
  isActive,
  isSelected,
}: AssetProps) {
  const { formFeedBack, size } = useFeedbackTool();
  const isImage = isImageFile(attachment.name);
  const [openImg, setOpenImg] = useState(false);
  const { handleDownload, isLoading: isDownloading } = useHandleDownload();

  const deleteAttachment = useDeleteFeedbackAttachment(
    attachment?.attachmentId ?? 0,
    formFeedBack?.parentId ?? 0,
  );
  const isDeleting = useIsMutating({
    mutationKey: feedbackToolKeys.deleteAttachment(attachment.storyId),
  });

  if (!isImage) {
    return (
      <div
        className={`${size === "MODAL" ? "w-55" : ""} mb-2 border pt-2 pb-2 pl-2 pr-2 flex justify-between items-center rounded-sm border-border-secondary ${isActive ? "" : "opacity-50"}`}
      >
        <p className="text-sm text-text-secondary font-medium truncate max-w-60">
          {attachment.name}
        </p>
        <div className="flex items-center">
          {isDeleting ? (
            <Loading className="w-5 h-5" />
          ) : isSelected && formFeedBack?.type === "EDIT" ? (
            <Trash2
              className="text-[#d92d20] cursor-pointer mr-1 ml-1"
              onClick={() => deleteAttachment.mutate()}
              size={20}
            />
          ) : null}
          {isDownloading ? (
            <Loading className="w-5 h-5" />
          ) : (
            <Download
              className="text-text-brand-primary cursor-pointer"
              onClick={() =>
                handleDownload(
                  attachment.attachmentId,
                  attachment.url,
                  attachment.name,
                )
              }
              size={20}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {openImg ? (
        <ImageModal
          alt={attachment.name}
          onClose={() => setOpenImg(false)}
          src={attachment.url}
        />
      ) : null}
      <div
        className={`${size === "MODAL" ? "w-55" : ""} ${isActive ? "" : "opacity-50"}`}
      >
        <div className="border border-border-secondary p-2 mb-2 rounded-sm w-full cursor-pointer mt-2">
          <img
            alt={attachment.name}
            className="object-cover cursor-pointer"
            onClick={() => setOpenImg(true)}
            src={attachment.url}
          />
          <div
            className={`pt-1 pb-1 flex justify-between items-center rounded-sm mt-1 ${isActive ? "" : "opacity-50"}`}
          >
            <p className="text-sm text-text-secondary font-medium truncate max-w-60">
              {attachment.name}
            </p>
            <div className="flex items-center">
              {isDeleting ? (
                <Loading className="w-5 h-5" />
              ) : isSelected && formFeedBack?.type === "EDIT" ? (
                <Trash2
                  className="text-[#d92d20] cursor-pointer mr-1 ml-1"
                  onClick={() => deleteAttachment.mutate()}
                  size={20}
                />
              ) : null}
              {isDownloading ? (
                <Loading className="w-5 h-5" />
              ) : (
                <Download
                  className="text-text-brand-primary cursor-pointer"
                  onClick={() =>
                    handleDownload(
                      attachment.attachmentId,
                      attachment.url,
                      attachment.name,
                    )
                  }
                  size={20}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

interface ImageModalProps {
  alt?: string;
  onClose: () => void;
  src?: string;
}

function ImageModal({ alt = "", onClose, src }: ImageModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white hover:text-button-primary-bg cursor-pointer"
        onClick={onClose}
      >
        <X size={32} />
      </button>
      <img
        alt={alt}
        className="max-w-full max-h-full object-contain shadow-lg"
        src={src}
      />
    </div>
  );
}
